/* eslint-disable no-console */

const LOGGED_METHODS = new Set([
  'event',
  'identify',
  'setUserVars',
  'setVars',
  'shutdown',
  'restart',
  'consent',
  'anonymize',
  'log',
]);

/**
 * Wraps an FS-shaped object in a Proxy that logs calls to instrumentation
 * methods before forwarding them to the real implementation (if present).
 */
export function wrapWithLoggingProxy(target: object): typeof window.FS {
  return new Proxy(target, {
    get(obj, prop, receiver) {
      const value = Reflect.get(obj, prop, receiver);

      if (typeof prop !== 'string' || !LOGGED_METHODS.has(prop)) {
        return value;
      }

      return (...args: unknown[]) => {
        console.debug('[FullStory]', prop, ...args);

        if (typeof value === 'function') {
          return value.apply(obj, args);
        }
      };
    },
  }) as typeof window.FS;
}

/**
 * When FULLSTORY_LOGGING is enabled and FullStory itself is disabled, installs
 * a stub Proxy on window.FS so instrumentation calls log to the console.
 *
 * When FullStory is enabled, this is a no-op — the FullStoryLoader component
 * calls wrapWithLoggingProxy once the real SDK is available.
 *
 * Call this as early as possible (module-level in ClientRoot) so the stub
 * is in place before any markBundleEvaluationTime / markAppEntry calls.
 */
export function installFullStoryLoggingStub(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const loggingEnabled = process.env.FULLSTORY_LOGGING === 'true';

  if (!loggingEnabled) {
    return;
  }

  window.FS = wrapWithLoggingProxy(window.FS ?? {});
}
